import React, { useEffect, useMemo, useRef, useState } from 'react';
/** @jsxRuntime classic */
/** @jsx jsxCustomEvent */
import jsxCustomEvent from '@micro-zoe/micro-app/polyfill/jsx-custom-event';
import { useRouteMatch } from 'react-router-dom';
import LOAD_STATUS from '@/constants/load-status';
import JSLoading from '@/components/js-loading';
import StatusError from '@/components/status-error';
import injectToMicroapp from '@/inject-to-microapp';
import history from '@/history';
import reportError from '@/common/report-error';
import useSlideMenuCollapsedChange from '@/hooks/useSlideMenuCollapsedChange';
import { loginStateExpiress } from '@/common/utils';

function statusComponent(status: string) {
  if (status === LOAD_STATUS.SUCCESS) return null;
  if (status === LOAD_STATUS.FAILED) return <StatusError />;
  if (status === LOAD_STATUS.ING) return <JSLoading />;
  return null;
}

interface MicroappPropsType {
  url: string;
  name: string;
  needLoading?: boolean;
  inhash?: string; // 是否在hash路由下
  basenameNoEndSlash?: boolean;
  [index: string]: any;
}

function completeUrl(url: string) {
  if (!url.startsWith('//')) return url;

  try {
    return new URL(url, location.origin).href;
  } catch (ex) {
    console.error(ex);
    return url;
  }
}

/**
 * 根据配置的path和对应的url生成path对应的basename
 *
 * @param path /:id/*
 * @param url /111/a/b/c
 * @returns /111/a/
 */
export function transformPathToUrl(path: string, url: string, noEndSlash = false) {
  const pathArr = path.split('/').filter((item) => !!item);
  const urlArr = url.split('/').filter((item) => !!item);
  if (pathArr[pathArr.length - 1] === '*') pathArr.pop();

  if (pathArr.length > urlArr.length) {
    console.warn('getBasename入参不合法', path, url, pathArr, urlArr);
    return path;
  }

  let basename = '/';

  pathArr.forEach((_, index) => {
    basename += urlArr[index] + '/';
  });

  return noEndSlash ? basename.slice(0, basename.length - 1) : basename;
}

export default function Microapp(props: MicroappPropsType) {
  const { data = {}, inhash, url, needLoading = true, basenameNoEndSlash = false, baseroute = '', ..._props } = props;
  const [loadingStatus, setLoadingStatus] = useState(LOAD_STATUS.ING);
  const { path, url: matchedUrl } = useRouteMatch();

  const basename = useMemo(() => {
    // 如果没有传入basename则自动生成
    // 如果path: /abc/* -> basename: /abc/
    // 如果paht: /a -> basename: /a/
    if (!data.basename) return transformPathToUrl(path, matchedUrl, basenameNoEndSlash);

    const basenameEndChar = data.basename[data.basename.length - 1];
    if (basenameEndChar !== '/' || !basenameNoEndSlash) return data.basename;

    return data.basename.slice(0, data.basename.length - 1)
  }, [data.basename, basenameNoEndSlash])

  // 计算baseroute【如果传了，以传的为准，如果没转以传的data里的basename为准，如果还是没有，就以计算的当前路径的basename为准】
  const computedBaseroute = baseroute || basename;

  // 为url: //xxx.xxx 补上协议
  const completedUrl = useMemo(() => {
    if (typeof url !== 'string') return url;
    return completeUrl(url);
  }, [url]);

  const [collapsedStatus, _, toggleSidemenuCollapsedSatus] = useSlideMenuCollapsedChange('close', 'open');
  // 让子应用可以监听父应用路由变化从而同步子应用路由
  const historyListeners = useRef<any>([]);
  useEffect(() => {
    return () => historyListeners.current.forEach((item: any) => item());
  }, [])

  const throwDataToMicroapp = useMemo(() => {
    return Object.assign({}, data, injectToMicroapp, {
      basename,
      toggleSidemenu: toggleSidemenuCollapsedSatus,
      toLogin: loginStateExpiress,
      listenHistoryChange(fn: any) {
        const unlistener = history.listen(pathInfo => {
          if (!(window.location.pathname + '/').includes(basename)) return
          return fn(pathInfo)
        });
        historyListeners.current.push(unlistener);
        return unlistener;
      },
    });
  }, [data, basename, toggleSidemenuCollapsedSatus, history]);

  useEffect(() => {
    if (!inhash) return;
    if ('#' + inhash === window.location.hash) return;

    history.replace(window.location.pathname + window.location.search + '#' + inhash);
  }, [inhash]);

  useEffect(() => {
    const titleDescriptor = Object.getOwnPropertyDescriptor(document, 'title');
    const _title = document.title;
    try {
      Object.defineProperty(document, 'title', {
        set(value) {
          console.warn('子应用禁止设置title', '设置title' + value + '失败');
        },
        get() {
          return _title;
        },
        configurable: true,
        enumerable: true,
      });
    } catch (ex) {
      console.error(ex);
    }

    return () => {
      try {
        if (titleDescriptor) Object.defineProperty(document, 'title', titleDescriptor);
        else {
          // @ts-ignore
          delete document.title;
        }
      } catch (ex) {
        console.error(ex);
      }
    };
  }, []);

  return (
    <>
      <micro-app
        key="a"
        inline
        onBeforemount={() => {
          setLoadingStatus(LOAD_STATUS.SUCCESS);
        }}
        basename={basename}
        baseroute={computedBaseroute}
        onError={(error: ErrorEvent) => {
          reportError('微前端加载出错', { name: props.name, message: error?.message, error });
          setLoadingStatus(LOAD_STATUS.FAILED);
        }}
        data={throwDataToMicroapp}
        url={completedUrl}
        {..._props}
      />
      {needLoading && statusComponent(loadingStatus)}
    </>
  );
}
